import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { Z_INDEX } from '../../styles/variables';
import { flexCenterVertical, hideInPrint } from '../../styles/mixins';
import { CloseIcon } from '../HBProTip/HBProTip';
import HBText from '../HBText/HBText';
import HBButton from '../HBButton/HBButton';
import { mediumUp, upToMedium } from '../../styles/breakpoints';
import spacing from '../../styles/spacing';

const MIN_HEIGHT_FOR_ICON_ALIGNMENT = '24px';

export const BannerBorderRadiusValue = css`2px 4px 4px 2px`;

export const BannerContainerStyle = styled.div<{
    top: number;
    sticky: boolean;
    margin: string;
    boxShadow: string;
    background: string;
    minHeight: string;
    zIndex?: number;
}>`
    width: 100%;
    border-radius: ${BannerBorderRadiusValue};
    display: flex;
    ${({ margin, background, boxShadow, minHeight, zIndex }) =>
        css`
            margin: ${margin || '0 auto 30px'};
            background-color: ${background || vars.colors.surface.container};
            box-shadow: ${boxShadow || vars.shadow['10']};
            min-height: ${minHeight || '50px'};
            z-index: ${zIndex || Z_INDEX.TOP};
        `};
    ${({ sticky, zIndex, top }) =>
        sticky &&
        css`
            top: ${top || 0}px;
            position: sticky;
            z-index: ${zIndex || Z_INDEX.TOP};
        `}
    ${hideInPrint}
`;

export const BannerContent = styled.div<{
    fontSize: string;
    textAlignment: string;
    contentPadding: string;
}>`
    display: flex;
    align-items: center;
    width: 100%;
    ${({ fontSize, contentPadding, textAlignment }) =>
        css`
            font-size: ${fontSize || '12px'};
            padding: ${contentPadding || `12px`};
            justify-content: ${textAlignment || 'flex-start'};
        `};
`;

export const BannerBar = styled.div<{ color: string }>`
    width: 4px;
    border-top-left-radius: inherit;
    border-bottom-left-radius: inherit;
    background-color: ${({ color }) => color || vars.colors.base.accent};
`;

export const Actions = styled.div<{ align: 'start' | 'end' }>`
    display: flex;
    align-self: ${({ align }) => align};
    gap: 12px;
`;

export const Container = styled.div<{ paddingLeft: string }>`
    display: flex;
    align-items: center;
    padding-left: ${({ paddingLeft }) => paddingLeft};
`;

export const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

export const StyledHBButton = styled(HBButton)<{
    isLink?: boolean;
}>`
    margin: 0;

    /* TODO: In the case of button-link action type we are using a Button, 
    but it's probably more correct to use some kind of link component, since button has height/padding. */
    ${({ isLink }) =>
        isLink &&
        css`
            ${upToMedium} {
                padding: unset;
                height: unset;
            }
        `}
`;

export const BannerContainer = styled.div<{
    accentLine: string;
    background: string;
    whiteBanner?: boolean;
    isMultiLineDesign: boolean;
    width?: string;
    enableCloseButton?: boolean;
    hasIcon?: boolean;
    topPadding?: string;
    margin?: string;
    zIndex?: number;
    gradientBorderWidth?: string;
}>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
    max-height: 160px;
    gap: 16px;
    overflow: hidden;
    border-radius: ${BannerBorderRadiusValue};

    width: ${({ width }) => width};
    margin: ${({ margin }) => margin};
    padding: 12px 12px 12px 16px;
    ${({ enableCloseButton }) =>
        enableCloseButton &&
        css`
            padding-right: 38px;
        `}

    // gradient accent line
    ${({ accentLine, gradientBorderWidth }) =>
        accentLine.includes('gradient')
            ? css`
                  &::after {
                      content: '';
                      position: absolute;
                      bottom: 0;
                      width: 4px;
                      height: ${gradientBorderWidth
                          ? `calc(100% - ${gradientBorderWidth} * 2)`
                          : '100%'};
                      left: ${gradientBorderWidth ? gradientBorderWidth : '0'};
                      top: ${gradientBorderWidth ? gradientBorderWidth : '0'};
                      background: ${accentLine};
                      border-top-left-radius: 2px;
                      border-bottom-left-radius: 2px;
                  }
              `
            : css`
                  border-left: 4px solid ${accentLine};
              `}
   

    ${({ isMultiLineDesign, enableCloseButton }) =>
        isMultiLineDesign &&
        css`
            padding: 16px;
            flex-direction: column;
            gap: 0px;

            ${Actions} {
                padding-top: 8px;
            }

            ${Container} {
                align-items: flex-start;
                align-self: self-start;
            }

            ${enableCloseButton &&
            css`
                ${TextContainer} {
                    padding-right: 30px;
                }
            `};
        `};

    ${({ isMultiLineDesign }) =>
        !isMultiLineDesign &&
        css`
            ${Actions} {
                align-self: initial;
            }
        `}

    ${({ whiteBanner, zIndex, topPadding }) =>
        whiteBanner &&
        css`
            background: ${vars.colors.surface.container};
            box-shadow: ${vars.shadow['10']};
            position: sticky;
            top: ${topPadding || '0px'};
            z-index: ${zIndex || Z_INDEX.TOP};
        `};

    ${({ whiteBanner, background }) =>
        !whiteBanner &&
        css`
            background: ${background};
        `};

    ${hideInPrint}
`;

export const Title = styled(HBText)`
    padding-bottom: 4px;
`;

export const StyledTopRow = styled.span`
    gap: ${spacing[4]};
    min-height: ${MIN_HEIGHT_FOR_ICON_ALIGNMENT};
    ${flexCenterVertical}
`;

export const Content = styled(HBText)``;

export const IconContainer = styled.div`
    padding-right: 8px;
    display: flex;
    align-items: center;
    min-height: ${MIN_HEIGHT_FOR_ICON_ALIGNMENT};

    i {
        display: flex;
    }
`;

export const Close = styled(CloseIcon)<{ $isMultiLineDesign: boolean }>`
    position: absolute;
    display: flex;
    cursor: pointer;

    ${mediumUp} {
        top: unset;
        right: 16px;

        ${({ $isMultiLineDesign }) =>
            $isMultiLineDesign &&
            css`
                top: 18px;
                right: 18px;
            `};
    }
`;
